import type { DataProvider, FieldElement } from '../../../shared/types';

import {
  type DataProviderTrackingData,
  DataProviderTriggerType,
  DataProviderType,
} from '../../../shared/utils/troubleshooting';
import { DATA_PROVIDER } from '../../FormBuilder';

/**
 * Determines the trigger type for a data provider API call
 */
export function determineTriggerType(
  currentPage: number,
  dataProviderHasDependencies: boolean,
  dependenciesChanged: boolean,
  isFirstLoad: boolean
): DataProviderTriggerType {
  if (currentPage > 0) {
    return DataProviderTriggerType.PAGINATION;
  }

  if (currentPage === 0 && dataProviderHasDependencies && dependenciesChanged) {
    return DataProviderTriggerType.PARENT_CHANGE;
  }

  if (currentPage === 0 && (isFirstLoad || !dataProviderHasDependencies)) {
    return DataProviderTriggerType.INITIAL_LOAD;
  }

  return DataProviderTriggerType.INITIAL_LOAD;
}

/**
 * Extracts parent fields information from dependencies
 */
export function extractParentFields(
  dependencies: Record<string, unknown> | undefined,
  canvasFields?: FieldElement[]
):
  | Array<{
      fieldId: string;
      fieldName: string;
      selectedValue: unknown;
    }>
  | undefined {
  if (!dependencies || Object.keys(dependencies).length === 0) {
    return undefined;
  }

  return Object.entries(dependencies).map(([fieldId, selectedValue]) => {
    const parentField = canvasFields?.find((f) => f.id === fieldId);

    return {
      fieldId,
      fieldName: parentField?.fieldName || fieldId,
      selectedValue,
    };
  });
}

/**
 * Determines the data provider type (HTTP or SQL)
 */
export function determineDataProviderType(
  dataProvider: DataProvider
): DataProviderType {
  if (dataProvider.type === DATA_PROVIDER.HTTP) {
    return DataProviderType.HTTP;
  }

  // Datastore uses the same preview/data pipeline as SQL for analytics buckets
  if (dataProvider.type === DATA_PROVIDER.DATASTORE) {
    return DataProviderType.SQL;
  }

  return DataProviderType.SQL;
}

/**
 * Extracts API tracking metadata from data provider
 */
export function extractApiTrackingMetadata(
  dataProvider: DataProvider,
  fieldId: string
): {
  apiUrl?: string;
  sqlIdentifier?: string;
  httpMethod?: 'GET' | 'POST' | 'PUT' | 'DELETE';
} {
  const apiUrl =
    dataProvider.type === DATA_PROVIDER.HTTP
      ? dataProvider.httpApi.resourceUrl
      : undefined;
  const sqlIdentifier =
    dataProvider.type === DATA_PROVIDER.SQL ? fieldId : undefined;
  const httpMethod =
    dataProvider.type === DATA_PROVIDER.HTTP
      ? (dataProvider.httpApi.method as 'GET' | 'POST' | 'PUT' | 'DELETE') ||
        'GET'
      : undefined;

  return {
    apiUrl,
    sqlIdentifier,
    httpMethod,
  };
}

/**
 * Checks if data provider has dependencies
 */
export function hasDependencies(dataProvider: DataProvider): boolean {
  return !!(
    dataProvider.dependencies &&
    Object.keys(dataProvider.dependencies).length > 0
  );
}

/**
 * Prepares tracking data for API trigger event
 */
export function prepareApiTrackingData(
  fieldId: string,
  fieldName: string,
  dataProvider: DataProvider,
  triggerType: DataProviderTriggerType,
  currentPage: number,
  dependencies: Record<string, unknown> | undefined,
  canvasFields?: FieldElement[],
  fieldState?: {
    isReadOnly?: boolean;
    isDisabled?: boolean;
    isRequired?: boolean;
  },
  urlSlug?: string
): Omit<DataProviderTrackingData, 'startTime' | 'status'> {
  const dataProviderType = determineDataProviderType(dataProvider);
  const parentFields = extractParentFields(dependencies, canvasFields);
  const { apiUrl, sqlIdentifier, httpMethod } = extractApiTrackingMetadata(
    dataProvider,
    fieldId
  );

  return {
    fieldId,
    fieldName,
    fieldState: fieldState || {
      isReadOnly: false,
      isDisabled: false,
      isRequired: false,
    },
    triggerType,
    dataProviderType,
    parentFields,
    apiUrl,
    sqlIdentifier,
    httpMethod,
    page: currentPage,
    pageSize:
      dataProvider.type === DATA_PROVIDER.HTTP
        ? (dataProvider.httpApi.pagination?.pageSize ?? 10)
        : 10,
    context: {
      urlSlug,
    },
  };
}
